/**
 * Pure logic for the dark-store "TP पिक करें" rack-first picking sheet
 * (PickTpShipmentsModal + PickTpManualConfirmModal).
 *
 * No React / React Native imports here on purpose — keeps this fully
 * unit-testable without a component renderer.
 *
 * Source of truth: docs/cp-features/darkstore-api-contract.md §3 in
 * citymall-services (POST get-pick-shipments-v3 / pick-tp-v3).
 */

import type { ScannerConfig } from "@utils/nativeCodeScanner";

export type ScanKind = "SCANNED" | "MANUAL";

export interface PickTpRack {
  rack_id: number;
  rack_no: string;
  rack_name: string;
  rack_code: string;
  shipment_count: number;
}

export interface PickTpShipment {
  shipment_id: string;
  rack_id: number;
}

/**
 * Optional, per scan step. A step with a `mode` locks the native scanner to QR
 * or BARCODE; a missing step / mode shows the on-screen QR | Barcode switcher
 * (labels on the ground are mixed).
 */
export interface PickTpScannerConfigs {
  rack?: ScannerConfig | null;
  shipment?: ScannerConfig | null;
}

export interface GetPickShipmentsV3Response {
  total: number;
  racks: PickTpRack[];
  shipments: PickTpShipment[];
  scanner_config?: PickTpScannerConfigs | null;
  /**
   * false = the DB may scan any shipment of the TP directly; a rack scan is
   * only a hint. Missing (older server) means true: the old rack-first flow.
   */
  rack_scan_required?: boolean | null;
}

/** Missing / null (older server) keeps the old rack-first behaviour. */
export const isRackScanRequired = (
  response: { rack_scan_required?: boolean | null } | null | undefined,
): boolean => response?.rack_scan_required !== false;

/** One entry per shipment_id once it has been scanned or manually confirmed. */
export type ShipmentScanState = Record<
  string,
  { scan_kind: ScanKind } | undefined
>;

export interface PickTpMeta {
  route_id: string;
  cx_user_id: string;
  order_ids: number[];
}

/**
 * Decision 9: a line reports all three outcomes with a quantity each, and
 * carries its own photos per reason. picked + not_found + damaged must equal
 * the ordered quantity.
 */
export type IssueReason = "not_found" | "item_damaged";

export const ISSUE_REASONS: IssueReason[] = ["not_found", "item_damaged"];

export type IssueImages = Partial<Record<IssueReason, string[]>>;

export interface PickTpItem {
  order_item_id: number;
  picked_quantity: number;
  not_found_quantity: number;
  damaged_quantity: number;
  issue_images: IssueImages;
}

/** What the sheet holds per line while the DB is marking it. */
export interface PickTpItemMarking {
  not_found: number;
  item_damaged: number;
  images: Record<IssueReason, string[]>;
}

export type PickTpItemsState = Record<number, PickTpItemMarking>;

export const EMPTY_ITEM_MARKING: PickTpItemMarking = {
  not_found: 0,
  item_damaged: 0,
  images: { not_found: [], item_damaged: [] },
};

export const getItemMarking = (
  state: PickTpItemsState,
  orderItemId: number,
): PickTpItemMarking => state[orderItemId] || EMPTY_ITEM_MARKING;

export const markedUnits = (marking: PickTpItemMarking): number =>
  (marking.not_found || 0) + (marking.item_damaged || 0);

/**
 * Same rule as the CP's marking screen (CustomerOrderItemIssues): the reasons
 * share one pool capped at the ordered quantity, so a qty-3 line can be 1
 * missing + 1 damaged but never 2 + 2. Returns the state unchanged when the
 * change would breach the cap or go below zero.
 */
export const changeIssueQuantity = ({
  state,
  orderItemId,
  reason,
  delta,
  quantity,
}: {
  state: PickTpItemsState;
  orderItemId: number;
  reason: IssueReason;
  delta: number;
  quantity: number;
}): PickTpItemsState => {
  const marking = getItemMarking(state, orderItemId);
  const next = (marking[reason] || 0) + delta;
  if (next < 0) return state;
  if (delta > 0 && markedUnits(marking) >= quantity) return state;
  return {
    ...state,
    [orderItemId]: {
      ...marking,
      [reason]: next,
      images: {
        ...marking.images,
        // Dropping to zero drops that reason's photos with it, so a cleared
        // line can't submit a stale image.
        ...(next === 0 ? { [reason]: [] } : {}),
      },
    },
  };
};

/** qty-1 lines toggle a reason on/off instead of stepping (SingleIssueTooltip). */
export const toggleIssueReason = ({
  state,
  orderItemId,
  reason,
}: {
  state: PickTpItemsState;
  orderItemId: number;
  reason: IssueReason;
}): PickTpItemsState => {
  const marking = getItemMarking(state, orderItemId);
  const isOn = (marking[reason] || 0) > 0;
  const other: IssueReason =
    reason === "not_found" ? "item_damaged" : "not_found";
  return {
    ...state,
    [orderItemId]: {
      not_found: 0,
      item_damaged: 0,
      ...(isOn ? {} : { [reason]: 1 }),
      images: {
        [reason]: isOn ? [] : marking.images[reason] || [],
        [other]: [],
      } as Record<IssueReason, string[]>,
    },
  };
};

export const setIssueImages = ({
  state,
  orderItemId,
  reason,
  urls,
}: {
  state: PickTpItemsState;
  orderItemId: number;
  reason: IssueReason;
  urls: string[];
}): PickTpItemsState => {
  const marking = getItemMarking(state, orderItemId);
  return {
    ...state,
    [orderItemId]: {
      ...marking,
      images: { ...marking.images, [reason]: urls },
    },
  };
};

export interface PickTpShipmentPayloadEntry {
  shipment_id: string;
  rack_id: number;
  scan_kind: ScanKind;
}

export interface PickTpShipmentsPayload extends PickTpMeta {
  mode: "SHIPMENTS";
  shipments: PickTpShipmentPayloadEntry[];
}

export interface PickTpItemsPayload extends PickTpMeta {
  mode: "ITEMS";
  items: PickTpItem[];
  shipments: PickTpShipmentPayloadEntry[];
}

const normalize = (value: string | null | undefined): string =>
  (value || "").trim().toUpperCase();

/**
 * A rack unlocks when the scanned/typed value equals its rack_code (QR
 * payload) or its rack_no (printed label / manual fallback).
 */
export const findRackByScanValue = (
  racks: PickTpRack[],
  value: string,
): PickTpRack | undefined => {
  const needle = normalize(value);
  if (!needle) return undefined;
  return racks.find(
    (rack) =>
      normalize(rack.rack_code) === needle ||
      normalize(rack.rack_no) === needle,
  );
};

/** Shipments that belong to a given rack, in the order the API returned them. */
export const shipmentsForRack = (
  shipments: PickTpShipment[],
  rackId: number,
): PickTpShipment[] => shipments.filter((s) => s.rack_id === rackId);

export const isShipmentScanned = (
  scanState: ShipmentScanState,
  shipmentId: string,
): boolean => Boolean(scanState[shipmentId]);

export const pendingShipmentsForRack = (
  shipments: PickTpShipment[],
  rackId: number,
  scanState: ShipmentScanState,
): PickTpShipment[] =>
  shipmentsForRack(shipments, rackId).filter(
    (s) => !isShipmentScanned(scanState, s.shipment_id),
  );

/** A shipment_id match, scoped to one rack. */
export const findShipmentInRack = (
  shipments: PickTpShipment[],
  rackId: number,
  value: string,
): PickTpShipment | undefined => {
  const needle = normalize(value);
  if (!needle) return undefined;
  return shipmentsForRack(shipments, rackId).find(
    (s) => normalize(s.shipment_id) === needle,
  );
};

/** A shipment_id match against every rack — used to tell "wrong rack" apart from "unknown code". */
export const findShipmentAnywhere = (
  shipments: PickTpShipment[],
  value: string,
): PickTpShipment | undefined => {
  const needle = normalize(value);
  if (!needle) return undefined;
  return shipments.find((s) => normalize(s.shipment_id) === needle);
};

export type ScanResolution =
  | { type: "shipment_scanned"; shipment: PickTpShipment }
  // rack_scan_required === false: a shipment scan resolved to its own rack.
  | { type: "shipment_done"; shipment: PickTpShipment; rackId: number }
  | { type: "rack_unlocked"; rack: PickTpRack }
  | { type: "already_done"; shipment: PickTpShipment }
  | { type: "wrong_rack" }
  | { type: "unknown" }
  | { type: "empty" };

/**
 * Resolves one scanned/typed value against the current sheet state.
 *
 * Rules (contract §3 + plan §6.3):
 *  - While a rack is active and still has pending shipments, only that
 *    rack's shipment ids are accepted; a shipment id that belongs to a
 *    DIFFERENT rack is rejected as "wrong_rack" (distinct toast from an
 *    unrecognised code).
 *  - Once the active rack has nothing pending (or no rack is active yet),
 *    a value is tried against every not-yet-active rack's rack_code /
 *    rack_no to unlock the next rack.
 *
 * With `rackScanRequired: false` the rack is a hint, not a gate: any pending
 * shipment of the TP resolves to "shipment_done" with its own rack_id (the
 * caller makes that rack active), a rack code just focuses that rack, and
 * "wrong_rack" never fires. Only unknown / already-scanned codes error.
 */
export const resolveScanValue = ({
  value,
  racks,
  shipments,
  scanState,
  activeRackId,
  rackScanRequired = true,
}: {
  value: string;
  racks: PickTpRack[];
  shipments: PickTpShipment[];
  scanState: ShipmentScanState;
  activeRackId: number | null;
  rackScanRequired?: boolean;
}): ScanResolution => {
  const trimmed = (value || "").trim();
  if (!trimmed) return { type: "empty" };

  if (!rackScanRequired) {
    const shipment = findShipmentAnywhere(shipments, trimmed);
    if (shipment) {
      if (isShipmentScanned(scanState, shipment.shipment_id)) {
        return { type: "already_done", shipment };
      }
      return { type: "shipment_done", shipment, rackId: shipment.rack_id };
    }
    const rack = findRackByScanValue(racks, trimmed);
    if (rack) return { type: "rack_unlocked", rack };
    return { type: "unknown" };
  }

  if (activeRackId != null) {
    const pending = pendingShipmentsForRack(shipments, activeRackId, scanState);
    if (pending.length > 0) {
      // Search the whole active rack (not just its pending shipments) so a
      // re-scan of an already-done box resolves to "already_done" instead of
      // silently re-reporting "shipment_scanned".
      const matchInActiveRack = findShipmentInRack(
        shipments,
        activeRackId,
        trimmed,
      );
      if (matchInActiveRack) {
        if (isShipmentScanned(scanState, matchInActiveRack.shipment_id)) {
          return { type: "already_done", shipment: matchInActiveRack };
        }
        return { type: "shipment_scanned", shipment: matchInActiveRack };
      }

      const matchElsewhere = findShipmentAnywhere(shipments, trimmed);
      if (matchElsewhere) {
        if (isShipmentScanned(scanState, matchElsewhere.shipment_id)) {
          return { type: "already_done", shipment: matchElsewhere };
        }
        return { type: "wrong_rack" };
      }

      return { type: "unknown" };
    }
  }

  const matchedRack = findRackByScanValue(racks, trimmed);
  if (matchedRack) {
    return { type: "rack_unlocked", rack: matchedRack };
  }

  const matchedShipmentElsewhere = findShipmentAnywhere(shipments, trimmed);
  if (matchedShipmentElsewhere) {
    if (isShipmentScanned(scanState, matchedShipmentElsewhere.shipment_id)) {
      return { type: "already_done", shipment: matchedShipmentElsewhere };
    }
    return { type: "wrong_rack" };
  }

  return { type: "unknown" };
};

/** Every shipment the API returned has been scanned or manually confirmed. */
export const isAllShipmentsScanned = (
  shipments: PickTpShipment[],
  scanState: ShipmentScanState,
): boolean =>
  shipments.length > 0 &&
  shipments.every((s) => isShipmentScanned(scanState, s.shipment_id));

export const scannedCount = (
  shipments: PickTpShipment[],
  scanState: ShipmentScanState,
): number =>
  shipments.filter((s) => isShipmentScanned(scanState, s.shipment_id)).length;

export const buildShipmentsPayloadEntries = (
  shipments: PickTpShipment[],
  scanState: ShipmentScanState,
): PickTpShipmentPayloadEntry[] =>
  shipments
    .filter((s) => isShipmentScanned(scanState, s.shipment_id))
    .map((s) => ({
      shipment_id: s.shipment_id,
      rack_id: s.rack_id,
      scan_kind: (scanState[s.shipment_id] as { scan_kind: ScanKind })
        .scan_kind,
    }));

export const buildPickTpShipmentsPayload = ({
  meta,
  shipments,
  scanState,
}: {
  meta: PickTpMeta;
  shipments: PickTpShipment[];
  scanState: ShipmentScanState;
}): PickTpShipmentsPayload => ({
  route_id: meta.route_id,
  cx_user_id: meta.cx_user_id,
  order_ids: meta.order_ids,
  mode: "SHIPMENTS",
  shipments: buildShipmentsPayloadEntries(shipments, scanState),
});

export const buildPickTpItemsPayload = ({
  meta,
  items,
  carriedShipments,
}: {
  meta: PickTpMeta;
  items: PickTpItem[];
  carriedShipments: PickTpShipmentPayloadEntry[];
}): PickTpItemsPayload => ({
  route_id: meta.route_id,
  cx_user_id: meta.cx_user_id,
  order_ids: meta.order_ids,
  mode: "ITEMS",
  items,
  shipments: carriedShipments || [],
});

/**
 * Turns the sheet's per-line marking into the contract's items[]. "मिला" is
 * the remainder — never entered by the DB — so the three always add up to the
 * ordered quantity, which is what the server re-checks.
 */
export const buildItemsFromState = (
  items: { order_item_id: number; expected_quantity: number }[],
  state: PickTpItemsState,
): PickTpItem[] =>
  items.map((item) => {
    const marking = getItemMarking(state, item.order_item_id);
    const notFound = marking.not_found || 0;
    const damaged = marking.item_damaged || 0;
    return {
      order_item_id: item.order_item_id,
      picked_quantity: Math.max(
        0,
        (Number(item.expected_quantity) || 0) - notFound - damaged,
      ),
      not_found_quantity: notFound,
      damaged_quantity: damaged,
      // Only damaged carries a photo (images_mandatory_for_samasya).
      issue_images: {
        item_damaged: marking.images.item_damaged || [],
      },
    };
  });

export const totalUnpickedUnits = (items: PickTpItem[]): number =>
  items.reduce(
    (sum, item) =>
      sum +
      (Number(item.not_found_quantity) || 0) +
      (Number(item.damaged_quantity) || 0),
    0,
  );

// ---------------------------------------------------------------------------
// Error-code → Hindi toast (contract §3 "Errors"). The server localises via
// appErrorML when a bundle entry exists; until the backend adds one, the
// fallback echoes the raw code as `message`, so this map is the safety net
// the app owns regardless of what the server sends.
// ---------------------------------------------------------------------------
export const PICK_TP_ERROR_MESSAGES: Record<string, string> = {
  NOT_ALL_SHIPMENTS_SCANNED:
    "सभी शिपमेंट अभी स्कैन नहीं हुए हैं। बाकी शिपमेंट स्कैन करें या मैन्युअल कन्फर्म करें।",
  SHIPMENT_NOT_IN_TP: "यह शिपमेंट इस TP का हिस्सा नहीं है।",
  PICK_EVIDENCE_REQUIRED: "मिसिंग आइटम के लिए कम से कम एक फोटो ज़रूरी है।",
  TP_NOT_RACKED:
    "यह TP अभी रैक नहीं हुआ है। CMO एग्ज़ीक्यूटिव से रैकिंग पूरी करवाएँ।",
  BAD_REQUEST: "कुछ गड़बड़ हो गई। दोबारा कोशिश करें।",
};

export const PICK_TP_DEFAULT_ERROR_MESSAGE =
  "पिक नहीं हो पाया। दोबारा कोशिश करें।";

/**
 * Reads an axios error thrown by getPickShipmentsV3 / pickTpV3 and returns a
 * Hindi toast. The backend's router-level handler
 * (cl-mark-deliveries-v3/cp.js) responds `{ error, message, data }` for a
 * CustomError — `error` is the raw code, `message` is either the localised
 * copy (if a bundle entry exists) or the same raw code echoed back.
 */
export const getPickTpErrorMessage = (err: unknown): string => {
  const response = (err as { response?: { data?: Record<string, unknown> } })
    ?.response;
  const data = response?.data;
  const code =
    (data?.error as string | undefined) ||
    (data?.message as string | undefined);

  if (code && PICK_TP_ERROR_MESSAGES[code]) {
    return PICK_TP_ERROR_MESSAGES[code];
  }

  const message = data?.message as string | undefined;
  const isRawCodeEcho =
    typeof message === "string" &&
    Object.keys(PICK_TP_ERROR_MESSAGES).includes(message);
  if (typeof message === "string" && message && !isRawCodeEcho) {
    return message;
  }

  return PICK_TP_DEFAULT_ERROR_MESSAGE;
};

/** Groups the API's flat shipments[] by rack_id, in racks[] order. */
export const groupShipmentsByRack = (
  racks: PickTpRack[],
  shipments: PickTpShipment[],
): Map<number, PickTpShipment[]> => {
  const map = new Map<number, PickTpShipment[]>();
  racks.forEach((rack) => {
    map.set(rack.rack_id, shipmentsForRack(shipments, rack.rack_id));
  });
  return map;
};

/**
 * A rack is "locked" in the UI when it is not the active rack and still has
 * pending shipments. A rack whose shipments are all done stays expanded
 * (shown fully ticked) even after the active pointer has moved on.
 */
export const isRackLocked = ({
  rack,
  shipments,
  scanState,
  activeRackId,
  rackScanRequired = true,
}: {
  rack: PickTpRack;
  shipments: PickTpShipment[];
  scanState: ShipmentScanState;
  activeRackId: number | null;
  rackScanRequired?: boolean;
}): boolean => {
  // Optional rack scan: nothing is gated; only the active rack is expanded
  // (see isRackExpanded), the others just show their progress.
  if (!rackScanRequired) return false;
  if (rack.rack_id === activeRackId) return false;
  const pending = pendingShipmentsForRack(shipments, rack.rack_id, scanState);
  return pending.length > 0;
};

export type ScanRejectType = "already_done" | "wrong_rack";

/**
 * What the native scanner should do with each code it could see on this step,
 * so a wrong label keeps it open instead of closing and costing a re-tap.
 * `accept` = codes that move the sheet forward (unlock a rack / confirm a box);
 * `reject` = known codes that would only bounce. Anything else is unknown.
 * Values are normalised (trimmed, upper-cased) like every matcher here.
 */
export const buildScanExpectations = ({
  racks,
  shipments,
  scanState,
  activeRackId,
  rackScanRequired = true,
}: {
  racks: PickTpRack[];
  shipments: PickTpShipment[];
  scanState: ShipmentScanState;
  activeRackId: number | null;
  rackScanRequired?: boolean;
}): { accept: string[]; reject: Record<string, ScanRejectType> } => {
  const candidates = new Set<string>();
  racks.forEach((rack) => {
    candidates.add(normalize(rack.rack_code));
    candidates.add(normalize(rack.rack_no));
  });
  shipments.forEach((s) => candidates.add(normalize(s.shipment_id)));
  candidates.delete("");

  const accept: string[] = [];
  const reject: Record<string, ScanRejectType> = {};
  candidates.forEach((value) => {
    const { type } = resolveScanValue({
      value,
      racks,
      shipments,
      scanState,
      activeRackId,
      rackScanRequired,
    });
    if (
      type === "rack_unlocked" ||
      type === "shipment_scanned" ||
      type === "shipment_done"
    ) {
      accept.push(value);
    } else if (type === "already_done" || type === "wrong_rack") {
      reject[value] = type;
    }
  });
  return { accept, reject };
};

/** Optional-rack-scan UI: expand the focused rack (the others show only a progress count). */
export const isRackExpanded = ({
  rack,
  activeRackId,
}: {
  rack: PickTpRack;
  activeRackId: number | null;
}): boolean => rack.rack_id === activeRackId;

export const scannedCountForRack = (
  shipments: PickTpShipment[],
  rackId: number,
  scanState: ShipmentScanState,
): number =>
  shipmentsForRack(shipments, rackId).filter((s) =>
    isShipmentScanned(scanState, s.shipment_id),
  ).length;
